import { useEffect, useState } from 'react';
import { useBookingsStore } from '../../store/bookingsStore';
import { useRefreshOnVisibility } from '../../hooks/useRefreshOnVisibility';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import TimeRangeSwitcher from './bookings/TimeRangeSwitcher';
import StatusFilterChips from './bookings/StatusFilterChips';
import BookingList from './bookings/BookingList';
import BookingDetailSheet from './bookings/BookingDetailSheet';
import BookingErrorState from './bookings/BookingErrorState';
import PullToRefreshIndicator from '../shared/PullToRefreshIndicator';
import type { Booking } from '../../types/provider';

export default function BookingsTab() {
  const {
    bookings,
    summary,
    range,
    statusFilter,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    errorMessage,
    setRange,
    setStatusFilter,
    refresh,
    loadMore,
    clearError,
  } = useBookingsStore();

  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const selectedBooking =
    selectedBookingId !== null
      ? bookings.find((b) => b.id === selectedBookingId) ?? null
      : null;

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useRefreshOnVisibility(refresh);

  const { containerRef: pullRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh: refresh });

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bl-surface-soft, #f7f8fa)',
      }}
    >
      {/* ─── Sticky header ─────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          background: 'var(--bl-surface, #fff)',
          padding: '12px 16px 10px',
          borderBottom: '1px solid var(--bl-divider, #ebebeb)',
          zIndex: 9,
        }}
      >
        <TimeRangeSwitcher value={range} onChange={setRange} />
        <div style={{ marginTop: 10 }}>
          <StatusFilterChips value={statusFilter} onChange={setStatusFilter} summary={summary} />
        </div>
      </div>

      {/* ─── Scrollable content ────────────────────────────────── */}
      <div
        ref={pullRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: 12,
          paddingBottom: 16,
          position: 'relative',
        }}
      >
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          progress={progress}
        />
        {error ? (
          <BookingErrorState
            kind={error}
            message={errorMessage}
            onRetry={() => {
              clearError();
              refresh();
            }}
          />
        ) : (
          <BookingList
            bookings={bookings}
            range={range}
            statusFilter={statusFilter}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onOpenDetail={(b: Booking) => setSelectedBookingId(b.id)}
          />
        )}
      </div>

      {/* ─── Detail sheet ──────────────────────────────────────── */}
      <BookingDetailSheet
        booking={selectedBooking}
        onClose={() => setSelectedBookingId(null)}
      />
    </div>
  );
}
